<script lang="ts">
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { cn } from '@/lib/utils';
  import { performMatch } from '@/Util/api';
  import { Button, buttonVariants } from '@/lib/components/ui/button';
  import * as Popover from '$lib/components/ui/popover';
  import { TagWrapper } from '@/Common';
  import { ActivityTag } from '@/Common/ActivityTag';
  import { calculateCompletedXSS, formatXSSDetails, XSSDeviationCategory, xssDeviationToCategory, type XSSDetails, type XSSDeviationDetails } from '@/Util/utils';
  import type { TodaysActivities } from '@/Common/constants';

  export let activity: Exercise;
  export let todaysActivities: TodaysActivities;
  export let disabled: boolean = false; // This controls whether the buttons on the card are disabled
  export let transparent: boolean = false; // This controls whether the buttons on the card are shown with transparent styling

  let matchingPopoverOpen: boolean = false;

  $: matchedActivity = activity.matched_activity ? todaysActivities.activities.find((a) => Activity.isMatchedAtoB(activity, a)) : null;
  // All activities matched to the same activity as this one
  $: matchedActivities = matchedActivity ? todaysActivities.activities.filter(a => Activity.isMatchedAtoB(a, matchedActivity)) : [];

  $: xssDetails = {
    xss: activity.xss ?? 0,
    xlss: activity.xlss ?? 0,
    xhss: activity.xhss ?? 0,
    xpss: activity.xpss ?? 0,
  }
  $: formattedXSSDetails = formatXSSDetails(xssDetails);
  $: plannedXSS = {
    xss: matchedActivity?.xss ?? 0,
    xlss: matchedActivity?.xlss ?? 0,
    xhss: matchedActivity?.xhss ?? 0,
    xpss: matchedActivity?.xpss ?? 0,
  };
  $: formattedPlannedXSS = formatXSSDetails(plannedXSS);

  $: completedXSS = calculateCompletedXSS(activity, matchedActivities);
  $: formattedCompletedXSS = completedXSS ? formatXSSDetails(completedXSS) : null;

  let xssDeviationCategory: XSSDetails<XSSDeviationDetails> | null = null;
  $: {
    if (matchedActivities && matchedActivities.length > 0 && plannedXSS && completedXSS) {
      xssDeviationCategory = xssDeviationToCategory(completedXSS, plannedXSS);
    } else {
      formattedCompletedXSS = null;
    }
  }

  function performMatchWrapper(a, target) {
    const targetPath = Activity.isForecast(target) ? 'forecast' : target?.path
    performMatch(a.path, targetPath);
    todaysActivities.update((prev) => {
      return prev.map((activity) => {
        if (activity.path == a.path) {
          return { ...activity, matched_activity: targetPath };
        } else if (activity.path == target?.path) {
          return { ...activity, num_matches: (activity.num_matches >= 0 ? activity.num_matches : 0) + 1 }; // Increment the number of matches for the head activity
        } else if (Activity.isMatchedAtoB(activity, a)) {
          return { ...activity, num_matches: (activity.num_matches > 0 ? activity.num_matches : 1) - 1 }; // Decrement the number of matches for the activity
        }
        return activity;
      });
    })
  }

  function performUnmatchWrapper(a) {
    performMatchWrapper(a, null);
  }

  function canMatch(a, target) {
    return !Activity.isPlanned(a) && (Activity.isPlanned(target) && (Activity.isForecast(target) || !Activity.hasMatch(target)));
  }

  $: canMatchList = todaysActivities.activities.filter((a) => canMatch(activity, a));

  const activityTag = ActivityTag('small');
  $: matchedActivityTags = matchedActivity ? [
    activityTag.forecast({ ...matchedActivity, state: matchedActivity.state }),
    !Activity.isForecast({ ...matchedActivity, state: matchedActivity.state }) ? activityTag.planned(matchedActivity) : null,
  ].filter((tag) => tag) : [];
  
</script>

<!-- Matching controls -->
{#if !Activity.isPlanned({ ...activity, scheduled: activity.scheduled, activity_mode: activity.activity_mode })
    && (canMatchList.length > 0 || matchedActivity)}
  {#if matchedActivity}
    <Popover.Root portal="#shadcn-ui-dialog-target" bind:open={matchingPopoverOpen}>
      <Popover.Trigger {disabled} class={cn(buttonVariants({ variant: 'outline', size: 'sm' }), transparent ? 'bg-transparent text-muted-foreground' : '')}>
        <TagWrapper tags={matchedActivityTags} />
        <span class='max-w-[120px] truncate ml-2'>{matchedActivity.name}</span>
        <i class={cn('fa-solid fa-lg ml-1',
          xssDeviationCategory.xss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
          xssDeviationCategory.xss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
          'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
        )} />
      </Popover.Trigger>
      <Popover.Content align="start" class="flex flex-col gap-4 p-3 w-auto min-w-[150px]">
        {#if formattedXSSDetails && formattedPlannedXSS && xssDeviationCategory}
          <div class="inline-grid text-xs font-medium w-full mt-1 max-w-[350px]" style="grid-template-columns: auto repeat(4, auto); gap:2px 12px;">
            <span>Target XSS</span>
            <span class='text-center tabular-nums'>{formattedPlannedXSS.xss}</span>
            <span class="text-center tabular-nums text-[var(--color-xlss)]">{formattedPlannedXSS.xlss}</span>
            <span class="text-center tabular-nums text-[var(--color-xhss)]">{formattedPlannedXSS.xhss}</span>
            <span class="text-center tabular-nums text-[var(--color-xpss)]">{formattedPlannedXSS.xpss}</span>

            <!-- Completed Activity Row(s) -->
            {#if matchedActivities.length > 1}
              <div class='col-span-5 border-0 border-solid border-t border-border' />
              {#each matchedActivities as ma}
                {@const formattedXSS = formatXSSDetails({
                  xss: ma.xss ?? 0,
                  xlss: ma.xlss ?? 0,
                  xhss: ma.xhss ?? 0,
                  xpss: ma.xpss ?? 0,
                })}
                <span class='whitespace-nowrap text-gray-600 max-w-[120px] truncate opacity-80'>{ma.name}</span>
                <span class='text-center tabular-nums opacity-80'>{formattedXSS.xss}</span>
                <span class="text-center tabular-nums text-[var(--color-xlss)] opacity-80">{formattedXSS.xlss}</span>
                <span class="text-center tabular-nums text-[var(--color-xhss)] opacity-80">{formattedXSS.xhss}</span>
                <span class="text-center tabular-nums text-[var(--color-xpss)] opacity-80">{formattedXSS.xpss}</span>
              {/each}
              <div class='col-span-5 border-0 border-solid border-t border-border' />
            {/if}
    
            <span>{matchedActivities.length > 1 ? 'Total ' : ''}Completed XSS</span>
            <span class='text-center tabular-nums'>{formattedCompletedXSS?.xss}</span>
            <span class="text-center tabular-nums text-[var(--color-xlss)]">{formattedCompletedXSS?.xlss}</span>
            <span class="text-center tabular-nums text-[var(--color-xhss)]">{formattedCompletedXSS?.xhss}</span>
            <span class="text-center tabular-nums text-[var(--color-xpss)]">{formattedCompletedXSS?.xpss}</span>

            <div />
            <div />
            <i class={cn('fa-solid fa-lg text-center',
              xssDeviationCategory.xlss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
              xssDeviationCategory.xlss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
              'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
            )} />
            <i class={cn('fa-solid fa-lg text-center',
              xssDeviationCategory.xhss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
              xssDeviationCategory.xhss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
              'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
            )} />
            <i class={cn('fa-solid fa-lg text-center',
              xssDeviationCategory.xpss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
              xssDeviationCategory.xpss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
              'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
            )} />
          </div>
        {/if}
        <Button size='sm' variant='outline' on:click={() => {
            matchingPopoverOpen = false;
            performUnmatchWrapper(activity);
          }} {disabled}
        >
          <i class='fa-solid fa-lg fa-unlink text-xs mr-1' /> 
          Unlink
        </Button>
      </Popover.Content>
    </Popover.Root>
  {:else if canMatchList.length > 0}
    {#if canMatchList.length === 1}
      <Button
        variant='outline'
        size='sm'
        class={cn(transparent ? 'bg-transparent text-muted-foreground' : '')}
        on:click={() => performMatchWrapper(activity, canMatchList[0])}
        {disabled}
      >
        <i class='fa-solid fa-lg fa-link text-xs mr-1' />
        <span class='truncate max-w-[120px]'>Match to {canMatchList[0].name}</span>
      </Button>
    {:else}
      <Popover.Root portal="#shadcn-ui-dialog-target" bind:open={matchingPopoverOpen}>
        <Popover.Trigger {disabled} class={cn(buttonVariants({ variant: 'outline', size: 'sm' }), transparent ? 'bg-transparent text-muted-foreground' : '')}>
          <i class='fa-solid fa-lg fa-link text-xs mr-1' />Match to...
        </Popover.Trigger>
        <Popover.Content align="start" class="flex flex-col gap-1 p-0 w-auto min-w-[150px]">
          {#each canMatchList as a}
            <Button
              variant="ghost"
              size='sm'
              class="flex w-full justify-start text-left"
              on:click={() => {
                matchingPopoverOpen = false;
                performMatchWrapper(activity, a);
              }}
            >
              <i class="fa-solid fa-lg fa-link text-xs mr-1" />
              <span class='truncate max-w-[120px]'>{a.name}</span>
            </Button>
          {/each}
        </Popover.Content>
      </Popover.Root>
    {/if}
  {/if}
{/if}
